/* eslint-disable react-hooks/exhaustive-deps */
import { Badge } from "@component/badge";
import Toast from "@component/toast";
import { Tooltip } from "@component/tooltip";
import { useUserProfileContext } from "@context/user_profile";
import { calculateSimiliarityScore, getCompetitiveIntelligence } from "@service/job";
import { CandidateJobCompetitiveIntelligence, Job } from "@type/job";
import { convertCurrency } from "@util/currency";
import { formatHumanReadable } from "@util/date";
import { getSubdomainHost } from "@util/name";
import { AwardIcon, Briefcase, StarIcon, UserPlusIcon } from "lucide-react";
import { useEffect, useState } from "react";

interface Props {
  job: Job;
  onClick: () => void;
}

export function JobCard(props: Props) {
  const [isLoadingUserProfile, userInfo] = useUserProfileContext();
  const [matchPercentageNumber, setMatchPercentageNumber] = useState<number>(0);
  const [matchPercentage, setMatchPercentage] = useState<string>("0%");
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastVisible, setToastVisible] = useState<boolean>(false);
  const [candidateJobCompetitiveIntelligence, setCandidateJobCompetitiveIntelligence] = useState<CandidateJobCompetitiveIntelligence | undefined>(undefined);

  useEffect(() => {
    if (!isLoadingUserProfile && userInfo) {
      calculateSimiliarityScore(props.job.id, userInfo?.id || "").then((res) => {
        if (res) {
          setMatchPercentageNumber(res.similarity_score);
          setMatchPercentage(res.percentage);
        }

      }).catch((err) => {
        console.error("Error fetching job similarity:", err);
      });

      getCompetitiveIntelligence(props.job.id, userInfo?.id || "").then((res) => {
        if (res) {
          setCandidateJobCompetitiveIntelligence(res.data.data);
        }
      }).catch((err) => {
        console.error("Error fetching competitive intelligence:", err);
      });
    }
  }, [isLoadingUserProfile, userInfo]);

  const handleCopyText = (e: React.MouseEvent) => {
    e.stopPropagation();
    const candidateProfileLink = `${getSubdomainHost(props.job.company_subdomain || "")}/job/${props.job.id}${userInfo?.referral_code ? `?referral=${userInfo.referral_code}` : ""}`;
    navigator.clipboard
      .writeText(candidateProfileLink)
      .then(() => {
        setToastVisible(true);
        setToastMessage("Referral link copied to clipboard!");
      })
      .catch((err) => {
        console.error("Could not copy text: ", err);
      });
  };

  return (
    <>
      <div
        className="rounded-xl p-3 shadow-sm cursor-pointer transition-colors outline outline-1 outline-gray-200 hover:outline-2 hover:outline-blue-600"
        onClick={props.onClick}
      >
        <div className="flex items-center gap-4">
          {props.job.company_name === "AirAsia" ? (
            <div className="w-11 h-11 rounded-md flex items-center justify-center flex-shrink-0 bg-gradient-to-br ">
              <img
                src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${props.job.company_logo}`}
              />
            </div>
          ) : (
            <>
              {props.job.is_special_job ? (
                <div className="w-11 h-11 rounded-md flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-blue-500 to-blue-700">
                  <StarIcon size={20} className="text-white" />
                </div>
              ) : (
                <div className="w-11 h-11 items-center justify-center shadow-sm rounded-lg bg-gray-100 text-gray-500 border flex">
                  <Briefcase className="w-5 h-5" />
                </div>
              )}
            </>
          )}

          <div className="flex flex-1 items-center">
            <div className="flex-1">
              <div className="flex justify-between">
                <h3 className="font-medium line-clamp-1">{props.job.job_title}</h3>
              </div>
              <div className="flex gap-3 text-xs text-gray-600 mt-1">
                {(!!props.job.location && props.job.location !== "") && <span className="hidden sm:flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-black"></span>
                  {props.job.location}
                </span>}
                {(!!props.job.job_type && props.job.job_type !== "") && <span className="hidden sm:flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-black"></span>
                  {props.job.job_type || "Full-time"}
                </span>}
                {(!!props.job.salary_range && props.job.salary_range !== "") && <span className="hidden sm:flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-black"></span>
                  {props.job.salary_range}
                </span>}
                <span className="text-gray-400">
                  {props.job.updated_at && new Date(props.job.updated_at) > new Date(props.job.created_at) ? `Updated ${formatHumanReadable(props.job.updated_at, true)}` : `Posted ${formatHumanReadable(props.job.created_at, true)}`}
                </span>
                {!!props.job.invited_at && props.job.invited_at !== "" && (
                  <span className="flex text-blue-600 items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-blue-600"></span>
                    Invited on {formatHumanReadable(props.job.invited_at, true)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-row space-x-2">
              {candidateJobCompetitiveIntelligence && candidateJobCompetitiveIntelligence.top_n <= 10 && (
                <Tooltip
                  content={`You are the top 10% for this role.`}
                  side="top"
                >
                  <Badge
                    className="bg-purple-100 text-purple-800 hover:bg-purple-100 border-purple-200 cursor-default flex items-center gap-1"
                    style={{ borderRadius: "1rem" }}
                  >
                    <AwardIcon className="h-3 w-3" />
                    Top 10%
                  </Badge>
                </Tooltip>
              )}
              {props.job.referral_bonus > 0 && (
                <Tooltip
                  content={`Earn ${convertCurrency(props.job.referral_bonus, props.job.referral_currency)} for a successful
                    referral. Click to copy referral link.`}
                  side="top"
                  delayDuration={0}
                >
                  <button
                    type="button"
                    onClick={handleCopyText}
                  >
                    <Badge
                      className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-blue-200"
                      style={{ borderRadius: "1rem" }}
                    >
                      <UserPlusIcon className="w-4 h-4 mr-1" />
                      {convertCurrency(props.job.referral_bonus, props.job.referral_currency)}
                    </Badge>
                  </button>
                </Tooltip>
              )}
              {matchPercentageNumber >= 70 && (
                <Tooltip
                  content={`You have a ${matchPercentage} match to this job.`}
                  side="top"
                  delayDuration={0}
                >
                  <Badge
                    className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200"
                    style={{ borderRadius: "1rem" }}
                  >
                    {matchPercentage} Match
                  </Badge>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div >

      <Toast
        message={toastMessage}
        visible={toastVisible}
        onClose={() => setToastVisible(false)}
      />
    </>
  );
}
